import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

const AudioContext = createContext(null)

export function AudioProvider({ children }) {
    const audioRef = useRef(new Audio())

    const [currentTrack, setCurrentTrack] = useState(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)

    useEffect(() => {
        const audio = audioRef.current

        const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0)
        const onLoadedMetadata = () => setDuration(audio.duration || 0)
        const onPlay = () => setIsPlaying(true)
        const onPause = () => setIsPlaying(false)
        const onEnded = () => setIsPlaying(false)

        audio.addEventListener('timeupdate', onTimeUpdate)
        audio.addEventListener('loadedmetadata', onLoadedMetadata)
        audio.addEventListener('play', onPlay)
        audio.addEventListener('pause', onPause)
        audio.addEventListener('ended', onEnded)

        return () => {
            audio.removeEventListener('timeupdate', onTimeUpdate)
            audio.removeEventListener('loadedmetadata', onLoadedMetadata)
            audio.removeEventListener('play', onPlay)
            audio.removeEventListener('pause', onPause)
            audio.removeEventListener('ended', onEnded)
        }
    }, [])

    const playTrack = useCallback((track) => {
        if (!track?.src) return

        const audio = audioRef.current
        const isSameTrack = currentTrack?.src === track.src

        if (!isSameTrack) {
            audio.src = track.src
            setCurrentTrack(track)
        }

        audio.play().catch((err) => {
            console.error('Playback failed:', err)
        })
    }, [currentTrack])

    const pauseTrack = useCallback(() => {
        audioRef.current.pause()
    }, [])

    const togglePlayPause = useCallback(() => {
        const audio = audioRef.current

        if (!audio.src) return

        if (audio.paused) {
            audio.play().catch((err) => {
                console.error('Playback failed:', err)
            })
        } else {
            audio.pause()
        }
    }, [])

    const seekTo = useCallback((time) => {
        const audio = audioRef.current
        audio.currentTime = time
        setCurrentTime(time)
    }, [])

    const value = useMemo(
        () => ({
            currentTrack,
            isPlaying,
            currentTime,
            duration,
            playTrack,
            pauseTrack,
            togglePlayPause,
            seekTo,
        }),
        [
            currentTrack,
            isPlaying,
            currentTime,
            duration,
            playTrack,
            pauseTrack,
            togglePlayPause,
            seekTo,
        ]
    )

    return (
        <AudioContext.Provider value={value}>
            {children}
        </AudioContext.Provider>
    )
}

export function useAudio() {
    const context = useContext(AudioContext)

    if (!context) {
        throw new Error('useAudio must be used inside AudioProvider')
    }

    return context
}